// RadarChart.tsx
// A custom lightweight React component that draws a 4-axis taste vector radar chart using SVG.
// Supports dual-series rendering: master data (dashed, light fill) + user data (solid, bold fill).
import React from 'react';

interface TasteVector {
  sweetness: number;
  sourness: number;
  coolness: number;
  fruitiness: number;
}

interface RadarChartProps {
  // 後方互換: userValuesが省略された場合に使われる値（既存の呼び出し元向け）
  sweetness: number;
  sourness: number;
  coolness: number;
  fruitiness: number;
  size?: number;
  /** マスターデータのベクトル。指定時は破線・薄塗りで描画 */
  masterValues?: TasteVector;
  /** 個人評価ベクトル。指定時は実線・濃塗りで描画（未指定は sweetness/sourness等から生成） */
  userValues?: TasteVector;
  /** 凡例を表示するか（デフォルト false） */
  showLegend?: boolean;
}

const AXIS_LABELS = ['甘さ', '酸味', '清涼感', '果実感'];
const NUM_AXES = 4;

// SVG viewBox 固定
const VB_SIZE = 220;
const CENTER = 110;
const MAX_RADIUS = 75;

function getCoordinates(index: number, value: number): { x: number; y: number } {
  const angle = (Math.PI * 2 * index) / NUM_AXES - Math.PI / 2;
  const normalizedVal = Math.max(0, Math.min(5, value));
  const r = (normalizedVal / 5) * MAX_RADIUS;
  return {
    x: CENTER + r * Math.cos(angle),
    y: CENTER + r * Math.sin(angle),
  };
}

function buildPolygonPoints(vector: TasteVector): string {
  const vals = [vector.sweetness, vector.sourness, vector.coolness, vector.fruitiness];
  return vals.map((v, i) => {
    const { x, y } = getCoordinates(i, v);
    return `${x},${y}`;
  }).join(' ');
}

// グリッドポリゴン
const gridLevels = [1, 2, 3, 4, 5];
const gridPolygons = gridLevels.map((level) =>
  Array.from({ length: NUM_AXES }, (_, i) => {
    const { x, y } = getCoordinates(i, level);
    return `${x},${y}`;
  }).join(' ')
);

// ラベル位置
const labelPositions = Array.from({ length: NUM_AXES }, (_, i) => {
  const angle = (Math.PI * 2 * i) / NUM_AXES - Math.PI / 2;
  const labelRadius = MAX_RADIUS + 18;
  return {
    x: CENTER + labelRadius * Math.cos(angle),
    y: CENTER + labelRadius * Math.sin(angle),
    name: AXIS_LABELS[i],
  };
});

export default function RadarChart({
  sweetness,
  sourness,
  coolness,
  fruitiness,
  size = 220,
  masterValues,
  userValues,
  showLegend = false,
}: RadarChartProps) {
  // 後方互換: userValuesが未指定ならプロップスから生成
  const resolvedUser: TasteVector = userValues ?? { sweetness, sourness, coolness, fruitiness };
  const hasMaster = masterValues !== undefined;

  const userPolygon = buildPolygonPoints(resolvedUser);
  const masterPolygon = hasMaster ? buildPolygonPoints(masterValues!) : null;

  const userVals = [resolvedUser.sweetness, resolvedUser.sourness, resolvedUser.coolness, resolvedUser.fruitiness];
  const masterVals = hasMaster
    ? [masterValues!.sweetness, masterValues!.sourness, masterValues!.coolness, masterValues!.fruitiness]
    : null;

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        margin: '10px 0',
        gap: '8px',
      }}
    >
      {/* 凡例 */}
      {showLegend && hasMaster && (
        <div
          style={{
            display: 'flex',
            gap: '16px',
            fontSize: '12px',
            color: 'var(--color-text)',
            opacity: 0.85,
          }}
        >
          <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <svg width="22" height="12" viewBox="0 0 22 12">
              <polygon
                points="2,10 11,2 20,10"
                fill="rgba(201, 166, 107, 0.18)"
                stroke="rgba(201, 166, 107, 0.7)"
                strokeWidth="1.5"
                strokeDasharray="3,2"
              />
            </svg>
            マスター
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <svg width="22" height="12" viewBox="0 0 22 12">
              <polygon
                points="2,10 11,2 20,10"
                fill="rgba(107, 78, 255, 0.35)"
                stroke="rgba(107, 78, 255, 0.9)"
                strokeWidth="2"
              />
            </svg>
            マイ評価
          </span>
        </div>
      )}

      {/* チャート本体 */}
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${VB_SIZE} ${VB_SIZE}`}
        style={{ overflow: 'visible' }}
      >
        {/* Background Grids */}
        {gridPolygons.map((points, idx) => (
          <polygon
            key={idx}
            points={points}
            fill="none"
            stroke="rgba(231, 228, 223, 0.15)"
            strokeWidth="1"
          />
        ))}

        {/* Axis dividers */}
        {Array.from({ length: NUM_AXES }).map((_, i) => {
          const { x, y } = getCoordinates(i, 5);
          return (
            <line
              key={i}
              x1={CENTER}
              y1={CENTER}
              x2={x}
              y2={y}
              stroke="rgba(231, 228, 223, 0.15)"
              strokeWidth="1"
            />
          );
        })}

        {/* Master polygon (破線・薄塗り) */}
        {hasMaster && masterPolygon && (
          <>
            <polygon
              points={masterPolygon}
              fill="rgba(201, 166, 107, 0.15)"
              stroke="rgba(201, 166, 107, 0.7)"
              strokeWidth="1.8"
              strokeDasharray="5,3"
            />
            {/* Master dots */}
            {masterVals!.map((val, i) => {
              const { x, y } = getCoordinates(i, val);
              return (
                <circle
                  key={`m-${i}`}
                  cx={x}
                  cy={y}
                  r="3.5"
                  fill="rgba(201, 166, 107, 0.8)"
                  stroke="var(--color-surface)"
                  strokeWidth="1"
                />
              );
            })}
          </>
        )}

        {/* User polygon (実線・濃塗り) */}
        <polygon
          points={userPolygon}
          fill="rgba(107, 78, 255, 0.30)"
          stroke="var(--color-primary)"
          strokeWidth="2.5"
        />
        {/* User dots */}
        {userVals.map((val, i) => {
          const { x, y } = getCoordinates(i, val);
          return (
            <circle
              key={`u-${i}`}
              cx={x}
              cy={y}
              r="4.5"
              fill="var(--color-accent)"
              stroke="var(--color-surface)"
              strokeWidth="1.5"
            />
          );
        })}

        {/* Text Labels */}
        {labelPositions.map((pos, i) => {
          let textAnchor = 'middle';
          if (pos.x < CENTER - 10) textAnchor = 'end';
          else if (pos.x > CENTER + 10) textAnchor = 'start';

          return (
            <text
              key={i}
              x={pos.x}
              y={pos.y}
              textAnchor={textAnchor}
              fill="var(--color-text)"
              fontSize="12px"
              fontFamily="var(--font-body-ja)"
              opacity="0.8"
            >
              {pos.name}
            </text>
          );
        })}
      </svg>
    </div>
  );
}
